/**
 * useBestOddsFilters - filter state, persistence, and derived filtered list
 * for the Best Odds / Market Anomalies product.
 *
 * Kept separate from arbitrage FilterContext. Persists to localStorage key
 * `bestOddsFilters` via bestOddsFilterPersistence.
 */

import { useState, useEffect, useCallback, useMemo } from 'react';
import {
  DEFAULT_BEST_ODDS_FILTERS,
  filterAnomalies,
  hasActiveBestOddsFilters
} from '../utils/bestOddsFilters';
import {
  loadBestOddsFilters,
  saveBestOddsFilters,
  normalizeBestOddsFilters
} from '../utils/bestOddsFilterPersistence';

/**
 * @param {Array} anomalies - full sorted anomalies list from BestOddsContext
 * @returns {Object} filter state, setters, and filteredAnomalies
 */
const useBestOddsFilters = (anomalies = []) => {
  const [filters, setFiltersState] = useState(() => loadBestOddsFilters());

  useEffect(() => {
    saveBestOddsFilters(filters);
  }, [filters]);

  const setFilters = useCallback((updater) => {
    setFiltersState((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      return normalizeBestOddsFilters(next);
    });
  }, []);

  const setMinSpread = useCallback((minSpread) => {
    setFilters((prev) => ({ ...prev, minSpread }));
  }, [setFilters]);

  const setMaxOdds = useCallback((maxOdds) => {
    setFilters((prev) => ({ ...prev, maxOdds }));
  }, [setFilters]);

  const setSearchQuery = useCallback((searchQuery) => {
    setFilters((prev) => ({ ...prev, searchQuery }));
  }, [setFilters]);

  const setSelectedBookmakers = useCallback((selectedBookmakers) => {
    setFilters((prev) => ({ ...prev, selectedBookmakers }));
  }, [setFilters]);

  const setSelectedChampionships = useCallback((selectedChampionships) => {
    setFilters((prev) => ({ ...prev, selectedChampionships }));
  }, [setFilters]);

  const setSelectedMarketKeys = useCallback((selectedMarketKeys) => {
    setFilters((prev) => ({ ...prev, selectedMarketKeys }));
  }, [setFilters]);

  const setTimeWindow = useCallback((timeWindow) => {
    setFilters((prev) => ({ ...prev, timeWindow }));
  }, [setFilters]);

  const resetFilters = useCallback(() => {
    setFiltersState({ ...DEFAULT_BEST_ODDS_FILTERS });
  }, []);

  const filteredAnomalies = useMemo(
    () => filterAnomalies(anomalies, filters),
    [anomalies, filters]
  );

  const hasActiveFilters = useMemo(
    () => hasActiveBestOddsFilters(filters),
    [filters]
  );

  return {
    filters,
    filteredAnomalies,
    filteredCount: filteredAnomalies.length,
    hasActiveFilters,
    setFilters,
    setMinSpread,
    setMaxOdds,
    setSearchQuery,
    setSelectedBookmakers,
    setSelectedChampionships,
    setSelectedMarketKeys,
    setTimeWindow,
    resetFilters
  };
};

export default useBestOddsFilters;
